import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, Heart } from "lucide-react";
import { formatSeconds, formatVertical } from "@/lib/measurements";
import LazyImage from "@/components/LazyImage";

export default function AthleteCard({ athlete, index = 0, isFavorite = false, onToggleFavorite = null, fromContext = "search" }) {
  const t = athlete.testing || {};
  const m = athlete.measurements || {};

  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onToggleFavorite) onToggleFavorite(athlete);
  };

  return (
    <Link
      to={`/athletes/${athlete.id}`}
      state={{ from: fromContext }}
      className={`bento-card group overflow-hidden hover:shadow-[0_8px_30px_rgba(0,229,255,0.08)] animate-fade-up stagger-${Math.min((index % 5) + 1, 5)} relative`}
      data-testid={`athlete-card-${athlete.id}`}
    >
      {onToggleFavorite && (
        <button
          onClick={handleFav}
          className={`absolute top-3 left-3 z-10 w-9 h-9 rounded-full backdrop-blur-md flex items-center justify-center transition-all ${
            isFavorite
              ? "bg-rose-500/20 text-rose-400 border border-rose-400/40"
              : "bg-black/40 text-white/70 border border-white/10 hover:bg-rose-500/15 hover:text-rose-300 hover:border-rose-400/40"
          }`}
          data-testid={`favorite-btn-${athlete.id}`}
          aria-label={isFavorite ? "Unfavorite" : "Favorite"}
        >
          <Heart size={15} fill={isFavorite ? "currentColor" : "none"} strokeWidth={2} />
        </button>
      )}
      <div className="relative aspect-[4/5] overflow-hidden bg-[#0a0a0a]">
        {athlete.profile_photo ? (
          <LazyImage
            src={athlete.profile_photo}
            alt={`${athlete.first_name} ${athlete.last_name}`}
            aspect="aspect-[4/5]"
            rounded=""
            className="absolute inset-0 !rounded-none"
            imgClass="transition-transform duration-700 group-hover:scale-105 opacity-90 group-hover:opacity-100"
            eager={index < 4}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0a0a0a] to-[#111]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        {athlete.verified_status && (
          <div className="absolute top-3 right-3">
            <div className="verified-badge">
              <ShieldCheck size={12} /> VERIFIED
            </div>
          </div>
        )}
        <div className="absolute bottom-3 left-3 right-3">
          <div className="label-mono">Class of {athlete.class_year || "—"}</div>
          <div className="font-display text-2xl font-black uppercase tracking-tight text-white leading-tight">
            {athlete.first_name}
            <br />
            {athlete.last_name}
          </div>
          <div className="flex items-center gap-2 text-xs text-white/60 mt-1">
            <span className="px-1.5 py-0.5 bg-electric/10 text-electric border border-electric/30 rounded font-display font-bold tracking-wider">
              {athlete.primary_position || "—"}
            </span>
            {athlete.city && (
              <span className="flex items-center gap-1"><MapPin size={11} />{athlete.city}, {athlete.state}</span>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 divide-x divide-white/5 border-t border-white/5">
        <Stat label="40-yd" value={t.forty_yard ? `${formatSeconds(t.forty_yard)}s` : "—"} />
        <Stat label="Vert" value={t.vertical ? formatVertical(t.vertical) : "—"} />
        <Stat label="Wt" value={m.weight ? `${m.weight}` : "—"} />
      </div>
    </Link>
  );
}

function Stat({ label, value }) {
  return (
    <div className="px-2 py-3 text-center">
      <div className="label-mono text-[9px]">{label}</div>
      <div className="font-display text-lg font-bold text-white">{value}</div>
    </div>
  );
}
